import { createSlice, createSelector } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";
import config from "../../config.json";
import { createRequestState, requestFailed, requestStarted, requestSucceeded } from "./requestState";

const slice = createSlice({
    name: "partners",
    initialState: {
        list: [],
        loading: false,
        lastFetch: null,
        partnerTypes: [],
        performers: [],
        filterList: [],
        agencies: [],
        subcontractorPartners: [],
        recruiterPartners: [],
        subcontractorCandidates: [],
        recruiterCandidates: [],
        agencyCandidates: [],
        partnerListByType: [],
        partnerListByRecType: [],
        applicationReportPartnerStats: {
            subcontractors: [],
            recruiters: [],
        },
        registeredPartners: [],
        partnerPerformers: [],
        requests: {
            list: createRequestState(),
            registered: createRequestState(),
            performersById: createRequestState(),
            filter: createRequestState(),
            types: createRequestState(),
            agencies: createRequestState(),
            subcontractorPartners: createRequestState(),
            recruiterPartners: createRequestState(),
            applicationReportPartnerStats: createRequestState(),
        },
    },
    reducers: {
        partnerRequestStarted: (partners, action) => {
            requestStarted(partners.requests, action.payload);
            partners.loading = true;
        },
        partnerRequestFailed: (partners, action) => {
            requestFailed(partners.requests, action.payload?.key, action.payload?.error || action.payload);
            partners.loading = false;
        },
        partnerAdded: (partners, action) => {
            partners.list.push(action.payload);
        },
        partnerReceived: (partners, action) => {
            partners.list = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "list");
        },
        agenciesReceived: (partners, action) => {
            partners.agencies = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "agencies");
        },
        filterPartnerReceived: (partners, action) => {
            partners.filterList = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "filter");
        },
        partnerTypesReceived: (partners, action) => {
            partners.partnerTypes = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "types");
        },
        partnerPerformersReceived: (partners, action) => {
            partners.performers = action.payload;
        },
        subcontractorPartnersRecevied: (partners, action) => {
            partners.subcontractorPartners = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "subcontractorPartners");
        },
        recruiterPartnersRecevied: (partners, action) => {
            partners.recruiterPartners = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "recruiterPartners");
        },
        subcontractorCandidatesRecevied: (partners, action) => {
            partners.subcontractorCandidates = action.payload;
        },
        recruiterCandidatesRecevied: (partners, action) => {
            partners.recruiterCandidates = action.payload;
        },
        agencyCandidatesRecevied: (partners, action) => {
            partners.agencyCandidates = action.payload;
        },
        partnerListByTypeRecevied: (partners, action) => {
            partners.partnerListByType = action.payload;
        },
        partnerListByRecTypeRecevied: (partners, action) => {
            partners.partnerListByRecType = action.payload;
        },
        applicationReportPartnerStatsReceived: (partners, action) => {
            const { type, data } = action.payload || {};
            if (type === "Sub-Contractors") {
                partners.applicationReportPartnerStats.subcontractors = Array.isArray(data) ? data : [];
            }
            if (type === "Recruiter") {
                partners.applicationReportPartnerStats.recruiters = Array.isArray(data) ? data : [];
            }
            partners.loading = false;
            requestSucceeded(partners.requests, `applicationReportPartnerStats:${type}`);
        },
        registeredPartnersRecevied: (partners, action) => {
            partners.registeredPartners = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "registered");
        },
        partnerUsersRecevied: (partners, action) => {
            partners.partnerPerformers = action.payload;
            partners.loading = false;
            requestSucceeded(partners.requests, "performersById");
        },
    }
});

export const { partnerRequestStarted, partnerRequestFailed,
    partnerAdded, partnerReceived, agenciesReceived, partnerTypesReceived,
    partnerPerformersReceived, filterPartnerReceived,
    subcontractorPartnersRecevied, recruiterPartnersRecevied,
    subcontractorCandidatesRecevied, recruiterCandidatesRecevied,
    agencyCandidatesRecevied, partnerListByTypeRecevied, partnerListByRecTypeRecevied, applicationReportPartnerStatsReceived, registeredPartnersRecevied,
    partnerUsersRecevied } = slice.actions;
export default slice.reducer;

export const loadPartners = (pageSize, currentPage) => apiCallBegan({
    url: config.SetupUsers.GetWorkflowRoles + pageSize + "&currentPage=" + currentPage,
    onStart: { type: partnerRequestStarted.type, payload: "list" },
    onSuccess: partnerReceived.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "list" } }
});

export const loadRegisteredPartners = () => apiCallBegan({
    url: config.GetRegisteredPartners,
    onStart: { type: partnerRequestStarted.type, payload: "registered" },
    onSuccess: registeredPartnersRecevied.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "registered" } },
    cache: true,
    cacheDuration: 60000
});

export const loadPartnerPerformersById = (id) => apiCallBegan({
    url: config.SetupUsers.GetPartnerPerformers + id,
    onStart: { type: partnerRequestStarted.type, payload: "performersById" },
    onSuccess: partnerUsersRecevied.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "performersById" } },
    cache: true,
    cacheDuration: 60000
});

export const loadPartnersByFilter = (data) => apiCallBegan({
    url: config.SetupUsers.GetWorkflowRolesByFilter,
    method: "post",
    data: data,
    onStart: { type: partnerRequestStarted.type, payload: "filter" },
    onSuccess: filterPartnerReceived.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "filter" } },
    cancelPrevious: true,
    cancelKey: "partners/filter"
});

export const loadPartnerTypes = (name) => apiCallBegan({
    url: `${config.GetCodemanager}${name}`,
    onStart: { type: partnerRequestStarted.type, payload: "types" },
    onSuccess: partnerTypesReceived.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "types" } },
    cache: true,
    cacheDuration: 300000
});

export const loadSubcontractorPartners = (type) => apiCallBegan({
    url: config.GetPartnerByType + type,
    onStart: { type: partnerRequestStarted.type, payload: "subcontractorPartners" },
    onSuccess: subcontractorPartnersRecevied.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "subcontractorPartners" } },
    cache: true,
    cacheDuration: 60000
});

export const loadAgencies = () => apiCallBegan({
    url: config.GetAgencyList,
    onStart: { type: partnerRequestStarted.type, payload: "agencies" },
    onSuccess: agenciesReceived.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "agencies" } },
    cache: true,
    cacheDuration: 60000
});

export const loadRecruiterPartners = (type) => apiCallBegan({
    url: config.GetPartnerByType + type,
    onStart: { type: partnerRequestStarted.type, payload: "recruiterPartners" },
    onSuccess: recruiterPartnersRecevied.type,
    onError: { type: partnerRequestFailed.type, payload: { key: "recruiterPartners" } },
    cache: true,
    cacheDuration: 60000
});

export const loadPartnerPerformers = (id) => apiCallBegan({
    url: `${config.GetPartnerUsers}${id}`,
    onSuccess: partnerPerformersReceived.type
});

export const loadSubcontractorCandidates = (id,type) => apiCallBegan({
    url: config.GetPartnerCandidates + id + "&type="+type,
    onSuccess: subcontractorCandidatesRecevied.type
});

export const loadRecruiterCandidates = (id) => apiCallBegan({
    url: config.GetPartnerCandidates + id + "&type=recruiter",
    onSuccess: recruiterCandidatesRecevied.type
});

export const loadAgencyCandidates = (id) => apiCallBegan({
    url: config.GetPartnerCandidates + id + "&type=recruiter",
    onSuccess: agencyCandidatesRecevied.type
});

export const loadPartnerListByType = (type, id) => apiCallBegan({
    url: config.GetPartnerList + type + "&id=" + id,
    onSuccess: partnerListByTypeRecevied.type
});

export const loadPartnerListByrecType = (type, id) => apiCallBegan({
    url: config.GetPartnerList + type + "&id=" + id,
    onSuccess: partnerListByRecTypeRecevied.type
});

export const loadApplicationReportPartnerStats = (type) => apiCallBegan({
    url: config.GetPartnerList + type + "&id=0",
    onStart: { type: partnerRequestStarted.type, payload: `applicationReportPartnerStats:${type}` },
    onSuccess: { type: applicationReportPartnerStatsReceived.type, payload: { type } },
    onError: { type: partnerRequestFailed.type, payload: { key: `applicationReportPartnerStats:${type}` } },
    cache: true,
    cacheDuration: 60000
});

export const getAllPartners = createSelector(
    state => state.entities.partners,
    partners => partners.list
);

export const getFilterPartners = createSelector(
    state => state.entities.partners,
    partners => partners.filterList
);

export const getPartnerTypes = createSelector(
    state => state.entities.partners,
    partners => partners.partnerTypes
);

export const getPartnerPerformers = createSelector(
    state => state.entities.partners,
    partners => partners.performers
);

export const getPartnerUsers = createSelector(
    state => state.entities.partners,
    partners => partners.partnerPerformers
);

export const getSubcontractorPartners = createSelector(
    state => state.entities.partners,
    partners => partners.subcontractorPartners
);
export const getRecruiterPartners = createSelector(
    state => state.entities.partners,
    partners => partners.recruiterPartners
);

export const getAgenciesPartners = createSelector(
    state => state.entities.partners,
    partners => partners.agencies
);

export const getSubcontractorCandidates = createSelector(
    state => state.entities.partners,
    partners => partners.subcontractorCandidates
);

export const getRecruiterCandidates = createSelector(
    state => state.entities.partners,
    partners => partners.recruiterCandidates
);

export const getAgencyCandidates = createSelector(
    state => state.entities.partners,
    partners => partners.agencyCandidates
);

export const getPartnerListByType = createSelector(
    state => state.entities.partners,
    partners => partners.partnerListByType
);

export const getApplicationReportPartnerStats = createSelector(
    state => state.entities.partners,
    partners => partners.applicationReportPartnerStats
);

export const partnerListByRecType = createSelector(
    state => state.entities.partners,
    partners => partners.partnerListByRecType
);

export const getRegisteredPartners = createSelector(
    state => state.entities.partners,
    partners => partners.registeredPartners
);

export const getPartnerRequests = createSelector(
    state => state.entities.partners,
    partners => partners.requests
);
